import React from "react";
import { Tag, Space, InputNumber, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import Datatable from "components/Common/dataTable";
import CustomButton from "components/Common/ui/CustomButton";
import { useTranslation } from "react-i18next";
import { useMatrixMarks } from "../hooks/useMatrixMarks";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import { useAbility } from "config/abilityContext";
import { all_routes } from "router/all_routes";
import CustomAlert from "components/Common/ui/CustomAlert";
import Loader from "components/Common/loader";
import CustomTooltip from "components/Common/ui/CustomTooltip";

type Props = {
  classId: string;
  subjectId: string;
  semester: string;
};

type RowData = any & { key: string };

const statusColor = (status: string) => {
  if (status === "active") return "green";
  if (status === "pending") return "gold";
  return "default";
};

const makeKey = (studentId: string, examSessionId: string) =>
  `${studentId}__${examSessionId}`;

const TeacherStudentExamTypeMatrixTable: React.FC<Props> = ({
  classId,
  subjectId,
  semester,
}) => {
  const { t } = useTranslation();
  const ability = useAbility();

  const { matrix, loading, saving, loadMatrix, saveTeacherMatrix } = useMatrixMarks();

  const [edits, setEdits] = React.useState<Record<string, number | null>>({});
  const hasChanges = Object.keys(edits).length > 0;

  React.useEffect(() => {
    if (!classId || !subjectId || !semester) return;
    loadMatrix(classId, subjectId, semester);
    setEdits({});
  }, [classId, subjectId, semester, loadMatrix]);

  const rows = React.useMemo<RowData[]>(
    () => (matrix?.students || []).map((s: any) => ({ ...s, key: s.id })),
    [matrix]
  );

  const cancelAll = () => setEdits({});

  const saveAll = async () => {
    if (!hasChanges) return;

    const payload = Object.entries(edits).map(([key, value]) => {
      const [studentId, examSessionId] = key.split("__");
      return {
        studentId,
        examSessionId,
        mark: value === null ? null : Number(value),
      };
    });

    try {
      await saveTeacherMatrix(payload as any);
      await loadMatrix(classId, subjectId, semester);
      setEdits({});
    } catch {
      message.error(t("save_failed"));
    }
  };

  const baseColumns: ColumnsType<RowData> = [
    {
      title: t("student"),
      dataIndex: "fullName",
      fixed: "left",
      width: 260,
      render: (_v, r) => (
        <UserProfileHeader
          firstName={r.firstName}
          lastName={r.lastName}
          photo={r.photo}
          subtitle={r.massarCode}
        />
      ),
    },
  ];

  const examSessionColumns: ColumnsType<RowData> = React.useMemo(() => {
    if (!matrix) return [];

    const examSessions = (matrix.examSessions || []).map((es: any) => ({
      id: es.id,
      name: es.name,
      examTypeName: es.examTypeName,
    }));

    const mm = matrix.matrix || {};

    return examSessions.map((es: any) => ({
      title: (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>
            {es.examTypeName}
          </div>
          <div style={{ fontSize: '12px', opacity: 0.8 }}>
            {es.name}
          </div>
        </div>
      ),
      key: es.id,
      width: 260,
      align: "center",

      render: (_v: any, row: any) => {
        const sessionMarks = mm[row.id]?.[es.id] || [];
        const mark = sessionMarks[0];

        const editKey = makeKey(row.id, es.id);

        const value =
          editKey in edits
            ? edits[editKey]
            : mark?.mark ?? null;

        const isPublished = mark?.status === "active";

        return (
          <Space>
            <InputNumber
              min={0}
              max={20}
              value={value as any}
              disabled={isPublished}
              onChange={(v) => {
                if (isPublished) return;
                setEdits((prev) => ({
                  ...prev,
                  [editKey]: v === null ? null : Number(v),
                }));
              }}
              style={{ width: 80 }}
            />

            {isPublished && (
              <CustomTooltip
                content={t("tooltips.mark_published")}
              />
            )}
          </Space>
        );
      },
    }));
  }, [matrix, edits, t]);

  const columns = React.useMemo(
    () => [...baseColumns, ...examSessionColumns],
    [baseColumns, examSessionColumns]
  );

  if (loading) {
    return <Loader />
  }

  if (examSessionColumns.length === 0) {
    return (
      <CustomAlert
        showIcon
        message={t("mark_matrix.no_exam_types.message")}
        description={t("mark_matrix.no_exam_types.description")}
        type="warning"
        asLink
        to={all_routes.examSession}
        buttonLabel={t("create_exam_session")}
        canShowButton={ability.can("getAll", "ExamSession")}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <CustomAlert
        showIcon
        message={t("mark_matrix.no_students.message")}
        description={t("mark_matrix.no_students.description")}
        asLink
        to={`${all_routes.detailClass.replace(":classId", classId)}?tab=students`}
        buttonLabel={t("add_student")}
        canShowButton={
          ability.can("create", "Student") &&
          ability.can("getAll", "Class")
        }
      />
    );
  }

  return (
    <div>
      <div className="d-flex mb-3 justify-content-end gap-2">
        <CustomButton disabled={!hasChanges || saving} onClick={saveAll}>
          {t("save")}
        </CustomButton>
        <CustomButton disabled={!hasChanges || saving} onClick={cancelAll}>
          {t("cancel")}
        </CustomButton>
      </div>

      <Datatable
        rowKey="key"
        columns={columns}
        dataSource={rows}
        loading={loading || saving}
        bordered
        scroll={{
          y: "calc(100vh - 220px)",
          x: "max-content",
        }}
      />
    </div>
  );
};

export default TeacherStudentExamTypeMatrixTable;